import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, MenuItem, TextField,
  Button, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Stack, Alert, Paper, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, Tab, Tabs, InputAdornment, LinearProgress
} from '@mui/material';
import {
  Inventory2, Add, Edit, Delete, Search, Refresh,
  Build, Warning, CheckCircle, Print, BarChart,
  FileDownload, QrCode, LocationOn, Person, Schedule,
  NewReleases, History
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { exportCSV } from '../utils/export';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('sms_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CONDITIONS  = ['new','good','fair','poor','damaged','under_repair'];
const STATUSES    = ['active','under_repair','disposed','lost','stolen','written_off'];
const ACQ_TYPES   = ['purchased','donated','government','leased','other'];
const MAINT_TYPES = ['routine','repair','inspection','upgrade','calibration','other'];
const MAINT_STATS = ['scheduled','in_progress','completed','cancelled'];
const DISPOSAL_METHODS = ['sold','donated','scrapped','returned','other'];

const COND_COLOR = { new:'success', good:'success', fair:'warning', poor:'error', damaged:'error', under_repair:'warning' };
const STAT_COLOR = { active:'success', under_repair:'warning', disposed:'default', lost:'error', stolen:'error', written_off:'default' };
const fmt  = n => n != null && n !== '' ? `E${parseFloat(n).toLocaleString('en', { minimumFractionDigits: 2 })}` : '—';
const fmtD = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const ptLabel = v => (v || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ── Overview Tab ───────────────────────────────────────────────────────────────
function OverviewTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get('/assets/stats')
      .then(r => { if (r.data.success) setStats(r.data.data); })
      .catch(() => enqueueSnackbar('Failed to load stats', { variant: 'error' }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const printRegister = (categoryId = '') => {
    const token = localStorage.getItem('sms_token');
    const params = new URLSearchParams({ token, status: 'active' });
    if (categoryId) params.set('category_id', categoryId);
    window.open(`${API_BASE}/assets/register?${params}`, '_blank');
  };

  if (loading) return <CircularProgress />;
  if (!stats) return null;
  const { summary: s, by_category, by_condition, upcoming_maintenance, recently_added, warranty_expiring } = stats;

  const depreciationPct = s.total_acquisition_cost > 0
    ? Math.round((s.total_current_value / s.total_acquisition_cost) * 100) : 100;

  return (
    <Box>
      <Stack direction="row" sx={{ mb: 2 }} justifyContent="flex-end" spacing={1}>
        <Button startIcon={<Print />} variant="outlined" size="small" onClick={() => printRegister()}>
          Print Full Register
        </Button>
      </Stack>

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Assets',     value: s.total_assets || 0,      color: '#0f2b4a' },
          { label: 'Active',           value: s.active || 0,            color: '#2e7d32' },
          { label: 'Under Repair',     value: s.under_repair || 0,      color: '#e65100' },
          { label: 'Poor/Damaged',     value: s.poor_condition || 0,    color: '#c62828' },
          { label: 'Total Cost',       value: fmt(s.total_acquisition_cost), color: '#1565c0' },
          { label: 'Current Value',    value: fmt(s.total_current_value), color: '#c9952a' },
        ].map(card => (
          <Grid item xs={6} sm={4} md={2} key={card.label}>
            <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${card.color}` }}>
              <CardContent sx={{ pb: '12px !important' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" fontSize={9}>{card.label}</Typography>
                <Typography variant="h5" fontWeight={800} sx={{ color: card.color, fontSize: 20 }}>{card.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Depreciation bar */}
      {s.total_acquisition_cost > 0 && (
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography fontWeight={700}>Portfolio Depreciation</Typography>
              <Typography fontSize={13}>Retained: {depreciationPct}% of original value</Typography>
            </Box>
            <LinearProgress variant="determinate" value={depreciationPct}
              color={depreciationPct >= 70 ? 'success' : depreciationPct >= 40 ? 'warning' : 'error'}
              sx={{ height: 10, borderRadius: 5 }} />
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
              <Typography fontSize={11} color="text.secondary">Current: {fmt(s.total_current_value)}</Typography>
              <Typography fontSize={11} color="text.secondary">Original: {fmt(s.total_acquisition_cost)}</Typography>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2}>
        {/* By category */}
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography fontWeight={700} gutterBottom>📦 Assets by Category</Typography>
              {by_category?.map((cat, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.6, borderBottom: '1px solid #f0f0f0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography fontSize={12}>{cat.category}</Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <Chip label={`${cat.count}`} size="small" sx={{ fontWeight: 700, fontSize: 10 }} />
                    {cat.total_cost > 0 && <Chip label={fmt(cat.total_cost)} size="small" color="primary" sx={{ fontSize: 10 }} />}
                    <Tooltip title="Print category register">
                      <IconButton size="small" onClick={() => printRegister(cat.category_id)}>
                        <Print sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* By condition */}
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography fontWeight={700} gutterBottom>🔍 Asset Condition</Typography>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {by_condition?.map((c, i) => (
                  <Grid item key={i}>
                    <Paper variant="outlined" sx={{ px: 1.5, py: 0.8, textAlign: 'center', borderRadius: 2 }}>
                      <Chip label={c.count} size="small" color={COND_COLOR[c.condition] || 'default'} sx={{ mb: 0.3, fontWeight: 700 }} />
                      <Typography fontSize={10} color="text.secondary">{ptLabel(c.condition)}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* Warranty expiring */}
              {warranty_expiring?.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography fontSize={12} fontWeight={700} color="warning.main" gutterBottom>⚠ Warranties Expiring (60 days)</Typography>
                  {warranty_expiring.map(a => (
                    <Box key={a.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
                      <Box>
                        <Typography fontSize={12} fontWeight={600}>{a.name}</Typography>
                        <Typography fontSize={10} color="text.secondary">{a.asset_tag} · {a.location || '—'}</Typography>
                      </Box>
                      <Chip label={fmtD(a.warranty_expiry)} size="small" color="warning" sx={{ fontSize: 10 }} />
                    </Box>
                  ))}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Upcoming maintenance */}
        {upcoming_maintenance?.length > 0 && (
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid #fca5a5' }}>
              <CardContent>
                <Typography fontWeight={700} gutterBottom color="error">🔧 Upcoming Maintenance (30 days)</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow>
                      {['Asset', 'Type', 'Due Date', 'Status', 'Location'].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
                      ))}
                    </TableRow></TableHead>
                    <TableBody>
                      {upcoming_maintenance.map(m => (
                        <TableRow key={m.id} hover>
                          <TableCell><Typography fontSize={12} fontWeight={600}>{m.asset_name}</Typography><Typography fontSize={10} color="text.secondary">{m.asset_tag}</Typography></TableCell>
                          <TableCell><Chip label={ptLabel(m.maintenance_type)} size="small" sx={{ fontSize: 10 }} /></TableCell>
                          <TableCell sx={{ fontSize: 11, color: 'error.main', fontWeight: 700 }}>{fmtD(m.next_due_date || m.scheduled_date)}</TableCell>
                          <TableCell><Chip label={ptLabel(m.status)} size="small" color={m.status === 'in_progress' ? 'warning' : 'default'} sx={{ fontSize: 10 }} /></TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{m.location || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Recently added */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography fontWeight={700} gutterBottom>🆕 Recently Added</Typography>
              <Grid container spacing={1.5}>
                {recently_added?.map(a => (
                  <Grid item xs={12} sm={6} md={4} key={a.id}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <Inventory2 sx={{ color: '#0f2b4a', mt: 0.3, fontSize: 20 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontSize={13} fontWeight={600} noWrap>{a.name}</Typography>
                        <Typography fontSize={10} color="text.secondary" fontFamily="monospace">{a.asset_tag}</Typography>
                        <Typography fontSize={11} color="text.secondary">{a.category_name} · {ptLabel(a.condition)}</Typography>
                        {a.location && <Typography fontSize={10} color="text.secondary">📍 {a.location}</Typography>}
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

// ── Asset Register Tab ─────────────────────────────────────────────────────────
function RegisterTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [assets, setAssets]       = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [filterCat, setCat]       = useState('');
  const [filterStatus, setStatus] = useState('active');
  const [filterCond, setCond]     = useState('');
  const [search, setSearch]       = useState('');
  const [dialog, setDialog]       = useState(false);
  const [editing, setEditing]     = useState(null);
  const [detail, setDetail]       = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [maintDialog, setMaintDialog] = useState(false);
  const [maintAsset, setMaintAsset] = useState(null);
  const [catDialog, setCatDialog] = useState(false);

  const BLANK = {
    asset_tag:'', name:'', description:'', category_id:'', acquisition_date:'',
    acquisition_cost:'', acquisition_type:'purchased', supplier:'', donor_name:'',
    serial_number:'', model:'', manufacturer:'', location:'', assigned_to:'',
    condition:'good', status:'active', useful_life_years:'', current_value:'',
    warranty_expiry:'', warranty_notes:'', notes:'',
    disposal_date:'', disposal_method:'', disposal_value:'', disposal_notes:''
  };
  const [form, setForm] = useState(BLANK);
  const setF = k => e => setForm(f => ({ ...f, [k]: e.target?.value ?? e }));

  const MAINT_BLANK = { asset_id:'', maintenance_type:'routine', description:'', scheduled_date:'', completed_date:'', performed_by:'', cost:'', status:'scheduled', next_due_date:'', notes:'' };
  const [maintForm, setMaintForm] = useState(MAINT_BLANK);
  const setMF = k => e => setMaintForm(f => ({ ...f, [k]: e.target?.value ?? e }));

  const [catForm, setCatForm] = useState({ name:'', description:'' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCat)    params.category_id = filterCat;
      if (filterStatus) params.status      = filterStatus;
      if (filterCond)   params.condition   = filterCond;
      if (search)       params.search      = search;
      const [ar, cr] = await Promise.all([
        api.get('/assets', { params }),
        api.get('/assets/categories')
      ]);
      if (ar.data.success) setAssets(ar.data.data);
      if (cr.data.success) setCategories(cr.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [filterCat, filterStatus, filterCond, search, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null); setForm(BLANK); setDialog(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      ...BLANK, ...a,
      acquisition_date: a.acquisition_date?.split('T')[0] || '',
      warranty_expiry:  a.warranty_expiry?.split('T')[0]  || '',
      disposal_date:    a.disposal_date?.split('T')[0]    || '',
      current_value:    a.current_value  || a.acquisition_cost || '',
    });
    setDialog(true);
  };

  const save = async () => {
    if (!form.asset_tag || !form.name)
      return enqueueSnackbar('Asset tag and name are required', { variant: 'warning' });
    try {
      if (editing) { await api.put(`/assets/${editing.id}`, form); enqueueSnackbar('Asset updated', { variant: 'success' }); }
      else { await api.post('/assets', form); enqueueSnackbar('Asset registered', { variant: 'success' }); }
      setDialog(false); setEditing(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this asset record?')) return;
    try { await api.delete(`/assets/${id}`); enqueueSnackbar('Deleted', { variant: 'success' }); load(); }
    catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const openDetail = async (id) => {
    setDetail(id); setDetailData(null);
    const r = await api.get(`/assets/${id}`);
    if (r.data.success) setDetailData(r.data.data);
  };

  const openMaint = (asset) => {
    setMaintAsset(asset);
    setMaintForm({ ...MAINT_BLANK, asset_id: asset.id });
    setMaintDialog(true);
  };

  const saveMaint = async () => {
    try {
      await api.post('/assets/maintenance', maintForm);
      enqueueSnackbar('Maintenance record added', { variant: 'success' });
      setMaintDialog(false);
      if (detail) openDetail(detail);
    } catch { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  const saveCat = async () => {
    try {
      await api.post('/assets/categories', catForm);
      enqueueSnackbar('Category created', { variant: 'success' });
      setCatDialog(false); setCatForm({ name:'', description:'' }); load();
    } catch { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  const isDisposed = form.status === 'disposed' || form.status === 'written_off' || form.status === 'lost' || form.status === 'stolen';

  return (
    <Box>
      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={3}>
          <TextField size="small" fullWidth placeholder="Search name, tag, serial, location…" value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField select size="small" fullWidth label="Category" value={filterCat} onChange={e => setCat(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField select size="small" fullWidth label="Status" value={filterStatus} onChange={e => setStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{ptLabel(s)}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField select size="small" fullWidth label="Condition" value={filterCond} onChange={e => setCond(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {CONDITIONS.map(c => <MenuItem key={c} value={c}>{ptLabel(c)}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="Refresh"><IconButton size="small" onClick={load}><Refresh /></IconButton></Tooltip>
          <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={() =>
            exportCSV('assets',
              ['Tag','Name','Category','Serial','Location','Assigned To','Condition','Status','Cost','Value','Warranty'],
              assets.map(a => [a.asset_tag, a.name, a.category_name||'', a.serial_number||'', a.location||'', a.assigned_to||'', a.condition, a.status, a.acquisition_cost||'', a.current_value||'', fmtD(a.warranty_expiry)])
            )
          }>CSV</Button>
          <Button size="small" variant="outlined" onClick={() => setCatDialog(true)}>+ Category</Button>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate} sx={{ bgcolor: '#0f2b4a', whiteSpace: 'nowrap' }}>Add Asset</Button>
        </Grid>
      </Grid>

      {/* Asset count */}
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <Chip label={`${assets.length} asset(s)`} size="small" />
        <Chip label={`${assets.filter(a => a.condition === 'poor' || a.condition === 'damaged').length} poor/damaged`} size="small" color="error" />
        <Chip label={`${assets.filter(a => a.status === 'under_repair').length} under repair`} size="small" color="warning" />
      </Stack>

      {loading ? <CircularProgress /> : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer sx={{ maxHeight: 560 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Asset Tag','Name & Category','Location','Assigned To','Condition','Status','Cost / Value','Actions'].map(h => (
                    <TableCell key={h} sx={{ bgcolor: '#0f2b4a', color: 'white', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {assets.length === 0 && (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <Inventory2 sx={{ fontSize: 48, color: 'text.disabled', mb: 1, display: 'block', mx: 'auto' }} />
                    <Typography color="text.secondary">No assets found. Click "Add Asset" to register your first item.</Typography>
                  </TableCell></TableRow>
                )}
                {assets.map((a, i) => (
                  <TableRow key={a.id} hover
                    sx={{ bgcolor: a.status === 'under_repair' ? '#fff8f0' : a.condition === 'poor' || a.condition === 'damaged' ? '#fff3f3' : i % 2 === 0 ? 'white' : '#fafafa', cursor: 'pointer' }}
                    onClick={() => openDetail(a.id)}>
                    <TableCell>
                      <Typography fontSize={12} fontWeight={700} fontFamily="monospace" color="primary">{a.asset_tag}</Typography>
                      {a.serial_number && <Typography fontSize={9} color="text.secondary">S/N: {a.serial_number}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={12} fontWeight={600}>{a.name}</Typography>
                      <Typography fontSize={10} color="text.secondary">{a.category_name || '—'}</Typography>
                      {a.model && <Typography fontSize={10} color="text.secondary">{a.manufacturer ? `${a.manufacturer} ` : ''}{a.model}</Typography>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{a.location || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{a.assigned_to || '—'}</TableCell>
                    <TableCell>
                      <Chip label={ptLabel(a.condition)} size="small" color={COND_COLOR[a.condition] || 'default'} sx={{ fontSize: 10 }} />
                    </TableCell>
                    <TableCell>
                      <Chip label={ptLabel(a.status)} size="small" color={STAT_COLOR[a.status] || 'default'} sx={{ fontSize: 10 }} />
                    </TableCell>
                    <TableCell>
                      {a.acquisition_cost && <Typography fontSize={11} color="text.secondary">Cost: {fmt(a.acquisition_cost)}</Typography>}
                      {a.current_value    && <Typography fontSize={12} fontWeight={600} color="#c9952a">{fmt(a.current_value)}</Typography>}
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Stack direction="row" spacing={0.3}>
                        <Tooltip title="Log maintenance">
                          <IconButton size="small" color="warning" onClick={() => openMaint(a)}><Build fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(a)}><Edit fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => del(a.id)}><Delete fontSize="small" /></IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Asset Detail Dialog */}
      <Dialog open={!!detail} onClose={() => { setDetail(null); setDetailData(null); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#0f2b4a', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Inventory2 />
          {detailData?.name || 'Asset Detail'}
          <Box sx={{ flexGrow: 1 }} />
          <Chip label={detailData?.asset_tag} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontFamily: 'monospace' }} />
        </DialogTitle>
        <DialogContent dividers>
          {!detailData ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : (
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary" display="block">Category</Typography><Typography fontSize={13}>{detailData.category_name || '—'}</Typography></Grid>
              <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary" display="block">Condition</Typography><Chip label={ptLabel(detailData.condition)} size="small" color={COND_COLOR[detailData.condition]} /></Grid>
              <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary" display="block">Status</Typography><Chip label={ptLabel(detailData.status)} size="small" color={STAT_COLOR[detailData.status]} /></Grid>
              <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary" display="block">Location</Typography><Typography fontSize={13}>{detailData.location || '—'}</Typography></Grid>
              {detailData.serial_number && <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary" display="block">Serial No.</Typography><Typography fontSize={12} fontFamily="monospace">{detailData.serial_number}</Typography></Grid>}
              {detailData.model && <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary" display="block">Model</Typography><Typography fontSize={12}>{detailData.manufacturer ? `${detailData.manufacturer} ` : ''}{detailData.model}</Typography></Grid>}
              {detailData.assigned_to && <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary" display="block">Assigned To</Typography><Typography fontSize={13}>{detailData.assigned_to}</Typography></Grid>}
              {detailData.acquisition_cost && <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary" display="block">Acquisition Cost</Typography><Typography fontSize={13} fontWeight={700}>{fmt(detailData.acquisition_cost)}</Typography></Grid>}
              {detailData.current_value && <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary" display="block">Current Value</Typography><Typography fontSize={13} fontWeight={700} color="#c9952a">{fmt(detailData.current_value)}</Typography></Grid>}
              {detailData.warranty_expiry && <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary" display="block">Warranty Expires</Typography><Typography fontSize={13}>{fmtD(detailData.warranty_expiry)}</Typography></Grid>}
              {detailData.description && <Grid item xs={12}><Typography variant="caption" color="text.secondary" display="block">Description</Typography><Typography fontSize={13}>{detailData.description}</Typography></Grid>}
              {detailData.notes && <Grid item xs={12}><Typography variant="caption" color="text.secondary" display="block">Notes</Typography><Typography fontSize={13}>{detailData.notes}</Typography></Grid>}

              {/* Maintenance history */}
              {detailData.maintenance?.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 0.5 }} />
                  <Typography fontWeight={700} fontSize={13} sx={{ mt: 1, mb: 0.5 }}>🔧 Maintenance History</Typography>
                  <Table size="small">
                    <TableHead><TableRow>
                      {['Type','Description','Date','Cost','Status','Next Due'].map(h => <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11 }}>{h}</TableCell>)}
                    </TableRow></TableHead>
                    <TableBody>
                      {detailData.maintenance.map(m => (
                        <TableRow key={m.id} hover>
                          <TableCell><Chip label={ptLabel(m.maintenance_type)} size="small" sx={{ fontSize: 10 }} /></TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{m.description}</TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{fmtD(m.completed_date || m.scheduled_date)}</TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{m.cost > 0 ? fmt(m.cost) : '—'}</TableCell>
                          <TableCell><Chip label={ptLabel(m.status)} size="small" color={m.status === 'completed' ? 'success' : m.status === 'in_progress' ? 'warning' : 'default'} sx={{ fontSize: 10 }} /></TableCell>
                          <TableCell sx={{ fontSize: 11, color: m.next_due_date && new Date(m.next_due_date) < new Date() ? 'error.main' : 'text.secondary' }}>{fmtD(m.next_due_date)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Grid>
              )}

              {/* Movement history */}
              {detailData.movements?.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 0.5 }} />
                  <Typography fontWeight={700} fontSize={13} sx={{ mt: 1, mb: 0.5 }}>📍 Location History</Typography>
                  {detailData.movements.map(m => (
                    <Box key={m.id} sx={{ display: 'flex', gap: 2, py: 0.4, fontSize: 12 }}>
                      <Typography fontSize={11} color="text.secondary" sx={{ minWidth: 80 }}>{fmtD(m.movement_date)}</Typography>
                      <Typography fontSize={11}>{m.moved_from || '—'} → <strong>{m.moved_to}</strong></Typography>
                      {m.moved_by_name && <Typography fontSize={11} color="text.secondary">by {m.moved_by_name}</Typography>}
                    </Box>
                  ))}
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDetail(null); setDetailData(null); }}>Close</Button>
          {detailData && (
            <>
              <Button variant="outlined" startIcon={<Build />} onClick={() => { openMaint(detailData); setDetail(null); }}>Log Maintenance</Button>
              <Button variant="outlined" onClick={() => { openEdit(detailData); setDetail(null); }}>Edit</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Add/Edit Asset Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ bgcolor: '#0f2b4a', color: 'white' }}>{editing ? `Edit Asset — ${editing.asset_tag}` : 'Register New Asset'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {/* Core identification */}
            <Grid item xs={12}><Typography variant="caption" fontWeight={700} color="primary">IDENTIFICATION</Typography></Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="Asset Tag / Barcode *" value={form.asset_tag} onChange={setF('asset_tag')} disabled={!!editing}
                InputProps={{ startAdornment: <InputAdornment position="start"><QrCode fontSize="small" /></InputAdornment> }} />
            </Grid>
            <Grid item xs={6} sm={5}>
              <TextField fullWidth size="small" label="Asset Name *" value={form.name} onChange={setF('name')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth size="small" label="Category" value={form.category_id} onChange={setF('category_id')}>
                <MenuItem value="">Select category…</MenuItem>
                {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Manufacturer" value={form.manufacturer} onChange={setF('manufacturer')} /></Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Model" value={form.model} onChange={setF('model')} /></Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Serial Number" value={form.serial_number} onChange={setF('serial_number')} /></Grid>

            {/* Acquisition */}
            <Grid item xs={12}><Divider /><Typography variant="caption" fontWeight={700} color="primary" sx={{ mt: 1, display: 'block' }}>ACQUISITION</Typography></Grid>
            <Grid item xs={6} sm={3}>
              <TextField select fullWidth size="small" label="How Acquired" value={form.acquisition_type} onChange={setF('acquisition_type')}>
                {ACQ_TYPES.map(t => <MenuItem key={t} value={t}>{ptLabel(t)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="date" label="Acquisition Date" value={form.acquisition_date} onChange={setF('acquisition_date')} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="Acquisition Cost (E)" value={form.acquisition_cost} onChange={setF('acquisition_cost')}
                InputProps={{ startAdornment: <InputAdornment position="start">E</InputAdornment> }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="Current Value (E)" value={form.current_value} onChange={setF('current_value')}
                InputProps={{ startAdornment: <InputAdornment position="start">E</InputAdornment> }} />
            </Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Supplier" value={form.supplier} onChange={setF('supplier')} /></Grid>
            {form.acquisition_type === 'donated' && (
              <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Donor Name" value={form.donor_name} onChange={setF('donor_name')} /></Grid>
            )}
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" type="number" label="Useful Life (years)" value={form.useful_life_years} onChange={setF('useful_life_years')} /></Grid>

            {/* Location & Status */}
            <Grid item xs={12}><Divider /><Typography variant="caption" fontWeight={700} color="primary" sx={{ mt: 1, display: 'block' }}>LOCATION & STATUS</Typography></Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Location" value={form.location} onChange={setF('location')} placeholder="e.g. Room 5, Science Lab" /></Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Assigned To" value={form.assigned_to} onChange={setF('assigned_to')} placeholder="Person or department" /></Grid>
            <Grid item xs={6} sm={2}>
              <TextField select fullWidth size="small" label="Condition" value={form.condition} onChange={setF('condition')}>
                {CONDITIONS.map(c => <MenuItem key={c} value={c}>{ptLabel(c)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField select fullWidth size="small" label="Status" value={form.status} onChange={setF('status')}>
                {STATUSES.map(s => <MenuItem key={s} value={s}>{ptLabel(s)}</MenuItem>)}
              </TextField>
            </Grid>

            {/* Warranty */}
            <Grid item xs={6} sm={4}><TextField fullWidth size="small" type="date" label="Warranty Expiry" value={form.warranty_expiry} onChange={setF('warranty_expiry')} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6} sm={8}><TextField fullWidth size="small" label="Warranty Notes" value={form.warranty_notes} onChange={setF('warranty_notes')} /></Grid>

            {/* Disposal (if disposing) */}
            {isDisposed && (
              <>
                <Grid item xs={12}><Divider /><Typography variant="caption" fontWeight={700} color="error" sx={{ mt: 1, display: 'block' }}>DISPOSAL</Typography></Grid>
                <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="date" label="Disposal Date" value={form.disposal_date} onChange={setF('disposal_date')} InputLabelProps={{ shrink: true }} /></Grid>
                <Grid item xs={6} sm={3}>
                  <TextField select fullWidth size="small" label="Disposal Method" value={form.disposal_method} onChange={setF('disposal_method')}>
                    {DISPOSAL_METHODS.map(m => <MenuItem key={m} value={m}>{ptLabel(m)}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth size="small" type="number" label="Disposal Value (E)" value={form.disposal_value} onChange={setF('disposal_value')}
                    InputProps={{ startAdornment: <InputAdornment position="start">E</InputAdornment> }} />
                </Grid>
                <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Disposal Notes" value={form.disposal_notes} onChange={setF('disposal_notes')} /></Grid>
              </>
            )}

            <Grid item xs={12}><TextField fullWidth size="small" label="General Notes" value={form.notes} onChange={setF('notes')} multiline rows={2} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Description" value={form.description} onChange={setF('description')} multiline rows={2} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} sx={{ bgcolor: '#0f2b4a' }}>{editing ? 'Update Asset' : 'Register Asset'}</Button>
        </DialogActions>
      </Dialog>

      {/* Maintenance Dialog */}
      <Dialog open={maintDialog} onClose={() => setMaintDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Build /> Log Maintenance — {maintAsset?.name}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Type" value={maintForm.maintenance_type} onChange={setMF('maintenance_type')}>
                {MAINT_TYPES.map(t => <MenuItem key={t} value={t}>{ptLabel(t)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Status" value={maintForm.status} onChange={setMF('status')}>
                {MAINT_STATS.map(s => <MenuItem key={s} value={s}>{ptLabel(s)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Description *" value={maintForm.description} onChange={setMF('description')} multiline rows={2} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="date" label="Scheduled Date" value={maintForm.scheduled_date} onChange={setMF('scheduled_date')} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="date" label="Completed Date" value={maintForm.completed_date} onChange={setMF('completed_date')} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Performed By" value={maintForm.performed_by} onChange={setMF('performed_by')} /></Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Cost (E)" value={maintForm.cost} onChange={setMF('cost')}
                InputProps={{ startAdornment: <InputAdornment position="start">E</InputAdornment> }} />
            </Grid>
            <Grid item xs={12}><TextField fullWidth size="small" type="date" label="Next Service Due" value={maintForm.next_due_date} onChange={setMF('next_due_date')} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Notes" value={maintForm.notes} onChange={setMF('notes')} multiline rows={2} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMaintDialog(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={saveMaint} startIcon={<Build />}>Save Maintenance Record</Button>
        </DialogActions>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog open={catDialog} onClose={() => setCatDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Asset Category</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}><TextField fullWidth size="small" label="Category Name *" value={catForm.name} onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Description" value={catForm.description} onChange={e => setCatForm(f => ({ ...f, description: e.target.value }))} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveCat} sx={{ bgcolor: '#0f2b4a' }}>Create Category</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Maintenance Tab ────────────────────────────────────────────────────────────
function MaintenanceTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [records, setRecords]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [filterStatus, setStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/assets/maintenance/all', { params: { status: filterStatus || undefined } });
      if (r.data.success) setRecords(r.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [filterStatus, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/assets/maintenance/${id}`, { status });
      enqueueSnackbar('Updated', { variant: 'success' }); load();
    } catch { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField select size="small" label="Status" value={filterStatus} onChange={e => setStatus(e.target.value)} sx={{ width: 160 }}>
          <MenuItem value="">All</MenuItem>
          {MAINT_STATS.map(s => <MenuItem key={s} value={s}>{ptLabel(s)}</MenuItem>)}
        </TextField>
        <Chip label={`${records.filter(r => r.status === 'scheduled' || r.status === 'in_progress').length} open`} size="small" color="warning" />
        <Chip label={`${records.filter(r => r.next_due_date && new Date(r.next_due_date) < new Date() && r.status !== 'completed').length} overdue`} size="small" color="error" />
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Refresh"><IconButton size="small" onClick={load}><Refresh /></IconButton></Tooltip>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Asset','Type','Description','Scheduled','Completed','Cost','Next Due','Status',''].map(h => (
                    <TableCell key={h} sx={{ bgcolor: '#0f2b4a', color: 'white', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No maintenance records found</TableCell></TableRow>}
                {records.map((m, i) => {
                  const overdue = m.next_due_date && new Date(m.next_due_date) < new Date() && m.status !== 'completed';
                  return (
                    <TableRow key={m.id} hover sx={{ bgcolor: overdue ? '#fff3f3' : i % 2 === 0 ? 'white' : '#fafafa' }}>
                      <TableCell>
                        <Typography fontSize={12} fontWeight={600}>{m.asset_name}</Typography>
                        <Typography fontSize={10} color="text.secondary" fontFamily="monospace">{m.asset_tag}</Typography>
                        <Typography fontSize={10} color="text.secondary">{m.category_name}</Typography>
                      </TableCell>
                      <TableCell><Chip label={ptLabel(m.maintenance_type)} size="small" sx={{ fontSize: 10 }} /></TableCell>
                      <TableCell sx={{ fontSize: 11, maxWidth: 200 }}>{m.description}</TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{fmtD(m.scheduled_date)}</TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{fmtD(m.completed_date)}</TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{m.cost > 0 ? fmt(m.cost) : '—'}</TableCell>
                      <TableCell sx={{ fontSize: 11, color: overdue ? 'error.main' : 'text.secondary', fontWeight: overdue ? 700 : 400 }}>
                        {fmtD(m.next_due_date)}{overdue && ' ⚠'}
                      </TableCell>
                      <TableCell>
                        <TextField select size="small" value={m.status} sx={{ minWidth: 110 }}
                          onChange={e => updateStatus(m.id, e.target.value)}>
                          {MAINT_STATS.map(s => <MenuItem key={s} value={s}>{ptLabel(s)}</MenuItem>)}
                        </TextField>
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>{m.performed_by || '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function AssetManagement() {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Inventory2 color="primary" /> Asset & Inventory Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Asset register · Condition tracking · Maintenance · Depreciation · Print register
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<BarChart fontSize="small" />}   label="Overview"    iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<Inventory2 fontSize="small" />} label="Register"    iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<Build fontSize="small" />}      label="Maintenance" iconPosition="start" sx={{ fontSize: 12 }} />
      </Tabs>

      <TabPanel value={tab} index={0}><OverviewTab /></TabPanel>
      <TabPanel value={tab} index={1}><RegisterTab /></TabPanel>
      <TabPanel value={tab} index={2}><MaintenanceTab /></TabPanel>
    </Box>
  );
}
